import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { environment } from 'src/environments/environment';
import { AnalyticsRequestObject } from '../model/interfaces/analytics-request-object.interface';
import { ProductCaptureData } from '../model/interfaces/product-capture.interface';
import { AnalyticsColumnProp } from '../product-capture-details/analytics/analytics-table-columns.const';
import { AnalyticsReportsTypes } from '../product-capture-details/analytics/analytics.conts';
import { AnalyticsDataHelper } from '../product-capture-details/analytics/analytics.helper';
import {
  ANALYTICS_SOURCE,
  ANALYTICS_TEST_PROPERTY,
  ANALYTICS_TYPE,
  AnalyticsData,
  AnalyticsTest,
  BackTestingTableData,
  BacktestedProbabilityOfLossTableData,
  ForwardLookingProbabilityOfLossTableData,
  ForwardTestingTableData,
  PRIIPsCostsTableData,
  PRIIPsReturnScenariosTableData,
  PRIIPsSummaryTableData,
  ProbabilityOfLossTableData,
  ProbabilityOfReturnsTableData,
  RiskRewardTableData,
  TableData,
  ValueForMoneyCloudRiskTableData,
  ValueForMoneyFVCTableData,
} from '../product-capture-details/analytics/analytics.interface';
import { NumberUtils } from '../utils/number.utils';

const API_ROOT_AS = environment.api.analyticsService;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(private apiService: ApiService) {}

  private HURDLE_AND_SPREAD_ENDPOINT = 'hurdle-and-spread';

  // << CRUD >>
  postAnalytics(id: number, source: AnalyticsRequestObject): Observable<any[]> {
    const payload = {
      productId: Number(id),
      ...source,
    };
    return this.apiService.post('', payload, API_ROOT_AS);
  }

  fetchAnalytics(productId: string): Observable<AnalyticsData[]> {
    return this.apiService.get(`${productId}`, API_ROOT_AS);
  }

  // << Helper functions (data handling, filtering, transforming) >>
  getDataBySourceAndType(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE,
    type: ANALYTICS_TYPE
  ): AnalyticsData | null {
    if (!data) {
      return null;
    }

    return (
      data.find((obj) => obj.source === source && obj.type === type) ?? null
    );
  }

  getRelevantTests(
    data: AnalyticsData,
    testNameExpression: string,
    property: ANALYTICS_TEST_PROPERTY
  ): AnalyticsTest[] | null {
    const tests = data?.tests;
    if (!tests) {
      return null;
    }
    const relevantTests = tests.filter((obj) =>
      obj[property]?.toLowerCase().includes(testNameExpression.toLowerCase())
    );
    return relevantTests;
  }

  getRelevantKey(test: AnalyticsTest, options: string[]): string | null {
    return (
      options.find((str) =>
        test?.metricDescription?.split(' ').join('').toLowerCase().includes(str)
      ) ?? null
    );
  }

  generateTableData(
    relevantTests: AnalyticsTest[],
    columnProps: AnalyticsColumnProp[],
    outcome: ANALYTICS_TEST_PROPERTY = ANALYTICS_TEST_PROPERTY.OUTCOME
  ): object[] {
    const tableData = [];

    relevantTests.forEach((obj: AnalyticsTest) => {
      const row = {};

      const existingRowIndex = tableData.findIndex(
        (dataEntry) => dataEntry.outcome === obj[outcome]
      );

      const keyToUpdate = this.getRelevantKey(obj, columnProps);

      if (existingRowIndex < 0 && keyToUpdate) {
        row['outcome'] = obj[outcome];
        row[keyToUpdate] = obj.metricValue;
        tableData.push(row);
      }
      if (existingRowIndex >= 0 && keyToUpdate) {
        tableData[existingRowIndex][keyToUpdate] = obj.metricValue;
      }
      if (existingRowIndex < 0 && !keyToUpdate) {
        row['outcome'] = obj[outcome];
        tableData.push(row);
      } else {
        return;
      }
    });
    return tableData;
  }

  transformProbabilityOfLossOutcome(str: string): string {
    const textRegex = /^P \(R >=/;
    const transform = textRegex.test(str);

    if (!transform) {
      return str;
    }

    const numberRegex = /\b\d+(\.\d+)?\b/g;
    const numberString = str.match(numberRegex);

    if (!numberString) {
      return str;
    }

    const percentString = parseFloat((Number(numberString) * 100).toFixed(3));
    return `Probability of loss greater than ${percentString}%`;
  }

  transformProbabilityOfLossProbability(value: string): string {
    if (!value) {
      return value;
    }
    return `${parseFloat((100 - Number(value)).toFixed(3)).toFixed(1)}%`;
  }

  transformAndMergeTableData = (tableData: any[]): any[] => {
    return tableData.reduce((acc: any[], row: any) => {
      let newRow = { ...row };

      if (newRow.outcomeDescription === '1Y') {
        newRow['exitAfterIHP'] = newRow.metricValue;
      } else if (newRow.outcomeDescription === 'RHP') {
        newRow['exitAfterRHP'] = newRow.metricValue;
      }

      let existingRow;

      if (newRow.testDescription === 'Cost') {
        existingRow = acc.find(
          (r) => r.testDescription === newRow.testDescription
        );
      } else {
        existingRow = acc.find(
          (r) =>
            r.testDescription === newRow.testDescription &&
            r.metricDescription === newRow.metricDescription
        );
      }

      if (existingRow) {
        if (newRow.exitAfterIHP) {
          existingRow['exitAfterIHP'] = newRow.exitAfterIHP;
        }
        if (newRow.exitAfterRHP) {
          existingRow['exitAfterRHP'] = newRow.exitAfterRHP;
        }
      } else {
        acc.push(newRow);
      }

      return acc;
    }, []);
  };

  // true = there is data but it's not all arrived yet so tables can't be built
  // false = there is data but there are errors, so we should next look for error messages
  isSourceDataAvailable(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE,
    type: AnalyticsReportsTypes
  ): boolean | null {
    if (
      !data?.length ||
      (data.length && source === ANALYTICS_SOURCE.CLOUD_RISK)
    ) {
      return false;
    }
    return !!data.find((obj) => {
      const isSameSource = obj?.source === source;
      const isPriipsAndTypeMatches =
        type === AnalyticsReportsTypes.PRIIPS &&
        obj?.type?.includes(AnalyticsReportsTypes.PRIIPS);
      const isStressAndTypeMatches =
        type === AnalyticsReportsTypes.STRESS &&
        !obj?.type?.includes(AnalyticsReportsTypes.PRIIPS);
      const isPending = obj?.status === 'Pending';
      const reportWasExecuted =
        obj?.integrationJsonResult?.version &&
        obj?.integrationJsonResult?.reportId;

      return (
        isSameSource &&
        (isPriipsAndTypeMatches || isStressAndTypeMatches) &&
        (isPending || reportWasExecuted)
      );
    });
  }

  hasRequestWithErrors(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE,
    type: AnalyticsReportsTypes
  ): boolean {
    if (
      !data?.length ||
      (data.length && source === ANALYTICS_SOURCE.CLOUD_RISK)
    ) {
      return false;
    }

    return !!data.find((obj) =>
      this.checkIfFVCReportHasErrors(obj, source, type)
    );
  }

  retrieveIntegrationJsonMessage(
    data: AnalyticsData[],
    type: AnalyticsReportsTypes
  ): string[] {
    if (!data) {
      return null;
    }

    const objWithMessage = new Set(
      data
        .filter((obj: AnalyticsData) =>
          this.checkIfFVCReportHasErrors(obj, ANALYTICS_SOURCE.FVC, type)
        )
        .map((obj: AnalyticsData) => obj.integrationJsonResult.message)
    );

    return objWithMessage?.size ? Array.from(new Set(objWithMessage)) : null;
  }

  private checkIfFVCReportHasErrors(
    obj: AnalyticsData,
    targetSource: ANALYTICS_SOURCE,
    type: AnalyticsReportsTypes
  ): boolean {
    const { integrationJsonResult } = obj;

    const isSameSource = obj.source === targetSource;

    if (!isSameSource) {
      return false;
    }

    const isPRIIPS =
      type === AnalyticsReportsTypes.PRIIPS && obj.type.includes(type);
    const isStressTesting =
      type === AnalyticsReportsTypes.STRESS &&
      !obj.type.includes(AnalyticsReportsTypes.PRIIPS);
    const isTypeMatch = isPRIIPS || isStressTesting;
    const reportHasMessageAndNoValidation =
      !!integrationJsonResult.message?.trim().length &&
      !integrationJsonResult.validation;
    const reportHasErrorMessages =
      !!integrationJsonResult.validation?.messages?.length &&
      integrationJsonResult.validation.outcome === 'error';

    return (
      isSameSource &&
      isTypeMatch &&
      (reportHasMessageAndNoValidation || reportHasErrorMessages)
    );
  }

  retrieveIntegrationJsonValidationMessages(
    data: AnalyticsData[],
    type: AnalyticsReportsTypes
  ): string[] | null {
    if (!data) {
      return null;
    }
    const objWithMessages = data.find((obj) =>
      this.checkIfFVCReportHasErrors(obj, ANALYTICS_SOURCE.FVC, type)
    );
    return objWithMessages
      ? [
          ...new Set(
            objWithMessages.integrationJsonResult?.validation?.messages
          ),
        ]
      : null;
  }

  getCreationDate(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE,
    type: ANALYTICS_TYPE
  ): string | null {
    if (!data) {
      return null;
    }
    const dataObj = data.find(
      (obj) => obj.source === source && obj.type === type && obj.dateCreated
    );
    return dataObj ? dataObj.dateCreated : null;
  }

  getFilteredColumnNamesBasedOnTableData(
    tableData: TableData[]
  ): AnalyticsColumnProp[] {
    const keys = [];
    tableData.forEach((obj) => keys.push(Object.keys(obj)));
    return [...new Set(keys.flat())];
  }

  // << Build table data >>
  getBackTestingTableData(data: AnalyticsData[]): BackTestingTableData[] {
    const backTestingData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.BACK_TYPE
    );

    if (!backTestingData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      backTestingData,
      'Call',
      ANALYTICS_TEST_PROPERTY.OUTCOME
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    const tableData = this.generateTableData(relevantTests, [
      'probability',
      'average',
    ]);
    return tableData;
  }

  getForwardTestingTableData(data: AnalyticsData[]): ForwardTestingTableData[] {
    const forwardTestingData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.FORWARD_TEST
    );

    if (!forwardTestingData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      forwardTestingData,
      'Call',
      ANALYTICS_TEST_PROPERTY.OUTCOME
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    const tableData = this.generateTableData(relevantTests, [
      'probability',
      'average',
    ]);
    return tableData;
  }

  getProbabilityOfReturnsTableData(
    data: AnalyticsData[]
  ): ProbabilityOfReturnsTableData[] {
    const probabilityOfReturnsData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.PERFORMANCE_TEST
    );

    if (!probabilityOfReturnsData) {
      return null;
    }

    const relevantTests = probabilityOfReturnsData?.tests;

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    const tableData = this.generateTableData(relevantTests, [
      'historical',
      'bear',
      'neutral',
      'bull',
      'increase',
      'decrease',
    ]);
    return tableData;
  }

  getBacktestedProbabilityOfLossTableData(
    data: AnalyticsData[]
  ): BacktestedProbabilityOfLossTableData[] {
    const backtestedProbabilityOfLossData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.BACK_TYPE
    );

    if (!backtestedProbabilityOfLossData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      backtestedProbabilityOfLossData,
      'Probability (%) of loss amount',
      ANALYTICS_TEST_PROPERTY.TEST
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    const tableData = this.generateTableData(relevantTests, [
      'last5',
      'last10',
      'last15',
    ]);
    return tableData;
  }

  getForwardLookingProbabilityOfLossTableData(
    data: AnalyticsData[]
  ): ForwardLookingProbabilityOfLossTableData[] {
    const forwardLookingProbabilityOfLossData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.FORWARD_TEST
    );

    if (!forwardLookingProbabilityOfLossData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      forwardLookingProbabilityOfLossData,
      'Probability (%) of loss amount',
      ANALYTICS_TEST_PROPERTY.TEST
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }
    const tableData = this.generateTableData(relevantTests, [
      'bear',
      'neutral',
      'bull',
      'decrease',
      'increase',
    ]);

    return tableData;
  }

  // NB this one overrides outcome to build the outcome column based on 'test' data
  getRiskRewardTableData(data: AnalyticsData[]): RiskRewardTableData[] {
    const riskRewardData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.RISK_REWARD
    );

    if (!riskRewardData) {
      return null;
    }

    const relevantTests1 = this.getRelevantTests(
      riskRewardData,
      'Payoff',
      ANALYTICS_TEST_PROPERTY.TEST
    );

    const relevantTests2 = this.getRelevantTests(
      riskRewardData,
      'Average',
      ANALYTICS_TEST_PROPERTY.TEST
    );

    const relevantTests = [...relevantTests1, ...relevantTests2].filter(
      (value: AnalyticsTest) =>
        value.testDescription !== 'Payoff percentile statistics'
    );

    if (!relevantTests?.length) {
      return null;
    }

    const tableData = this.generateTableData(
      relevantTests,
      ['bear', 'neutral', 'bull', 'increase', 'decrease', 'average'],
      ANALYTICS_TEST_PROPERTY.TEST
    );
    return tableData;
  }

  // NB this one also overrides outcome to build the outcome column based on 'test' data
  getValueForMoneyFVCTableData(
    data: AnalyticsData[]
  ): ValueForMoneyFVCTableData[] {
    const valueForMoneyFVCData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.FVC,
      ANALYTICS_TYPE.VFM_TEST
    );

    if (!valueForMoneyFVCData) {
      return null;
    }
    const relevantTests = this.getRelevantTests(
      valueForMoneyFVCData,
      'Greater than benchmark',
      ANALYTICS_TEST_PROPERTY.OUTCOME
    );

    if (!relevantTests?.length) {
      return null;
    }

    const tableData = this.generateTableData(
      relevantTests,
      ['bear', 'neutral', 'bull', 'decrease', 'increase'],
      ANALYTICS_TEST_PROPERTY.TEST
    );

    const benchmarkRate = this.getRelevantTests(
      valueForMoneyFVCData,
      'Deposit rate',
      ANALYTICS_TEST_PROPERTY.METRIC
    );

    if (benchmarkRate?.length && tableData?.length) {
      tableData[0]['hurdleRate'] = benchmarkRate[0].metricValue;
    }

    return tableData;
  }

  // NB this one transforms the table data before returning it
  getProbabilityOfLossTableData(
    data: AnalyticsData[]
  ): ProbabilityOfLossTableData[] {
    const probabilityOfLossData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.CLOUD_RISK,
      ANALYTICS_TYPE.FORWARD_TEST
    );

    if (!probabilityOfLossData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      probabilityOfLossData,
      'Probability of losing more than',
      ANALYTICS_TEST_PROPERTY.TEST
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    let mostRecentDupe = null;
    let currentValue = null;

    const tableData: ProbabilityOfLossTableData[] = relevantTests
      .sort((a, b) => {
        const aNum = NumberUtils.stringToNumber(a.testDescription);
        const bNum = NumberUtils.stringToNumber(b.testDescription);
        return aNum - bNum;
      })
      .reduce<any[]>((acc: any[], current: any) => {
        let { testDescription: outcome, metricValue: probability } = current;

        const outcomeAsNumber = NumberUtils.stringToNumber(outcome);
        const probabilityAsNumber = parseFloat(current.metricValue);

        if (!currentValue) {
          currentValue = probabilityAsNumber;
        }

        probability = probabilityAsNumber.toFixed(1) + '%';
        const currentMapped = { outcome, probability };

        if (probabilityAsNumber !== currentValue) {
          currentValue = probabilityAsNumber;
          mostRecentDupe && acc.push(mostRecentDupe);
          mostRecentDupe = null;
        } else {
          mostRecentDupe = currentMapped;
          return acc;
        }

        if (outcomeAsNumber >= 100) {
          return acc;
        }

        acc.push(currentMapped);
        return acc;
      }, []);

    return tableData;
  }

  getValueForMoneyCloudRiskTableData(
    data: AnalyticsData[]
  ): ValueForMoneyCloudRiskTableData[] {
    const valueForMoneyCloudRiskData = this.getDataBySourceAndType(
      data,
      ANALYTICS_SOURCE.CLOUD_RISK,
      ANALYTICS_TYPE.VFM_TEST
    );

    if (!valueForMoneyCloudRiskData) {
      return null;
    }

    const relevantTests = this.getRelevantTests(
      valueForMoneyCloudRiskData,
      'Return greater than benchmark',
      ANALYTICS_TEST_PROPERTY.OUTCOME
    );

    if (!relevantTests || !relevantTests.length) {
      return null;
    }

    const tableData = this.generateTableData(relevantTests, ['probability']);
    tableData.forEach(
      (data: any) =>
        (data.probability = `${Number(data.probability).toFixed(2)}%`)
    );

    const benchmarkRate = this.getRelevantTests(
      valueForMoneyCloudRiskData,
      'Benchmark rate',
      ANALYTICS_TEST_PROPERTY.METRIC
    );

    if (benchmarkRate?.length && tableData?.length) {
      tableData[0]['hurdleRate'] = benchmarkRate[0].metricValue;
    }

    return tableData;
  }

  getPRIIPsSummaryTableData(
    data: AnalyticsData[],
    TenorValue: string,
    source: ANALYTICS_SOURCE,
    product: ProductCaptureData
  ): PRIIPsSummaryTableData[] {
    const priipsSummaryData = this.getDataBySourceAndType(
      data,
      source,
      ANALYTICS_TYPE.PRIIPS_SUMMARY
    );

    if (!priipsSummaryData?.tests?.length) {
      return null;
    }

    priipsSummaryData.tests.push({
      metricDescription: 'Tenor',
      metricValue: TenorValue,
      outcomeDescription: '',
      testDescription: '',
    });

    priipsSummaryData.productCaptureDetails = product;

    return AnalyticsDataHelper.formatDataResults(priipsSummaryData, 'summary');
  }

  getPRIIPsReturnScenariosTableData(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE
  ): PRIIPsReturnScenariosTableData[] {
    const priipsReturnScenariosData = this.getDataBySourceAndType(
      data,
      source,
      ANALYTICS_TYPE.PRIIPS_SCENARIOS
    );

    if (!priipsReturnScenariosData?.tests?.length) {
      return null;
    }

    const tests = AnalyticsDataHelper.formatDataResults(
      priipsReturnScenariosData,
      'returnScenarios'
    );
    return this.transformAndMergeTableData(tests);
  }

  getPRIIPsCostsTableData(
    data: AnalyticsData[],
    source: ANALYTICS_SOURCE
  ): PRIIPsCostsTableData[] {
    const priipsCostsData = this.getDataBySourceAndType(
      data,
      source,
      ANALYTICS_TYPE.PRIIPS_COSTS
    );

    if (!priipsCostsData?.tests?.length) {
      return null;
    }

    const tests = AnalyticsDataHelper.formatDataResults(
      priipsCostsData,
      'costs'
    );
    return this.transformAndMergeTableData(tests);
  }

  getDefaultHurdleOrSpreadValue(
    productId: number,
    asOfDate: string
  ): Observable<{ spread?: number; hurdleRate?: number }> {
    return this.apiService.getWithParams(
      `${this.HURDLE_AND_SPREAD_ENDPOINT}/${productId}`,
      { asOfDate },
      API_ROOT_AS
    );
  }
}
